import { useEffect } from 'react'
import Link from 'next/link'
import { join, sum } from 'shared/utils/array'
import format from 'date-fns/format'
import { makePage } from '@/components/page'
import { useVenueContext } from '@/services/venue'
import { usePrice } from '@/components/price'
import { useAppContext } from '@/pages/_app'
import { HeadPanel } from '@/modern/components/head-panel'
import { useIsModernMenu } from '@/services/menu-design'
import { getCaptionByOrderMode } from '@/services/order'

export default makePage(
  {
    clientRenderOnly: {},
    meta: {
      subTitle: 'orders:myOrders',
      noIndex: true,
    },
  },
  function OrdersPage() {
    const { t } = useAppContext()
    const venueContext = useVenueContext()!
    const { venue, venueName, menuRoute, orderManager, locale } = venueContext
    const { orderHistory } = orderManager
    const price = usePrice({ useGrouping: true })
    const isModern = useIsModernMenu()

    // В списке — все заказы, которые сервер принял, а не только те, что
    // заведение уже подтвердило номером: иначе только что оформленный заказ
    // (и любой неподтверждённый) пропадал, и «Мои заказы» были пусты при
    // непустой истории.
    const entries = Array.from(orderHistory.entries())
      .filter(([, entry]) => {
        return !!entry.serverOrderUuid
      })
      .sort((a, b) => {
        return b[1].timestamp - a[1].timestamp
      })

    // статус неподтверждённых заказов обновляем при открытии списка: сам по
    // себе он обновлялся только на странице заказа
    useEffect(() => {
      Array.from(orderHistory.entries()).forEach(([key, entry]) => {
        if (!entry.serverOrderUuid || entry.serverOrder?.status.type === 'numberReady') {
          return
        }
        orderManager
          .getServerOrder({ serverOrderUuid: entry.serverOrderUuid })
          .then((serverOrder) => {
            if (serverOrder instanceof Error) {
              return undefined
            }
            return orderHistory.set(key, { ...entry, serverOrder })
          })
          .catch(() => {})
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const entryTotal = (entry: (typeof entries)[number][1]) => {
      return (
        entry.serverOrder?.prices.total ||
        sum(
          entry.orderedProducts.map((product) => {
            return Number(product.price) * Number(product.quantity)
          }),
        )
      )
    }

    if (!venueName || !venue) {
      return null
    }

    // новая тема: список заказов карточками прототипа (.ord-card)
    if (isModern) {
      return (
        <div>
          <HeadPanel url={menuRoute.path} text={t('orders:myOrders')} />
          <div className="ord-page ord-page--list">
            {/* пусто — это сообщение, а не поле формы: серая плашка во всю
                ширину читалась как погашенный ввод */}
            {entries.length === 0 ? (
              <div className="ord-empty">{t('orders:listIsEmpty')}</div>
            ) : (
              entries.map(([key, entry]) => {
                return (
                  <Link
                    className="ord-card"
                    key={key.clientUuid}
                    href={`/venue/${venueName}/order/${key.clientUuid}`}
                  >
                    {/* строки различимы: номер (когда заведение его выдало) и
                        режим заказа. Пока заказ не подтверждён, номера нет и в
                        ответе сервера — пишем, что он ждёт подтверждения */}
                    <div className="ord-card__top">
                      <b>
                        {entry.serverOrder?.status.type === 'numberReady'
                          ? `${t('common:anOrder')} №${entry.serverOrder.status.number}`
                          : t('common:anOrder')}
                        {' · '}
                        {getCaptionByOrderMode(t, entry.logistics.type, venue.features.serviceType)}
                      </b>
                      <span>{price.format(entryTotal(entry))}</span>
                    </div>
                    <div className="ord-card__sub">
                      {format(entry.timestamp, 'd MMMM yyyy HH:mm', { locale })} ·{' '}
                      {entry.orderedProducts.length} {t('orders:itms')}
                      {entry.serverOrder?.status.type !== 'numberReady' &&
                        ` · ${t('orders:waitingConfirmation')}`}
                    </div>
                  </Link>
                )
              })
            )}
          </div>
        </div>
      )
    }

    return (
      <div>
        <HeadPanel url={menuRoute.path} text={t('orders:myOrders')} />
        {orderHistory.size > 0 ? (
          <div>
            {join(
              Array.from(orderHistory.entries())
                .filter(([, entry]) => {
                  return entry.serverOrder && entry.serverOrder.status.type === 'numberReady'
                })
                .sort((a, b) => {
                  return b[1].timestamp - a[1].timestamp
                })
                .map(([key, entry]) => {
                  return (
                    <Link
                      key={key.clientUuid}
                      href={`/venue/${venueName}/order/${key.clientUuid}`}
                      legacyBehavior
                    >
                      <div className="px-5 py-3 cursor-pointer">
                        <div className="font-medium text-lg mb-2">
                          {format(entry.timestamp, 'd MMMM yyyy HH:mm', { locale })}
                        </div>
                        <div className="flex justify-between">
                          <div>
                            {entry.serverOrder?.status.type === 'numberReady'
                              ? `${t('common:anOrder')} №${entry.serverOrder.status.number}`
                              : `${t('common:anOrder')} (${entry.orderedProducts.length} ${t(
                                  'orders:itms',
                                )})`}
                          </div>
                          <div>
                            {price.format(
                              entry.serverOrder?.prices.total ||
                                sum(
                                  entry.orderedProducts.map((product) => {
                                    return Number(product.price) * Number(product.quantity)
                                  }),
                                ),
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  )
                }),
              <div className="mx-5 border-b-2 border-ghost dark:border-ghostDark"></div>,
            )}
          </div>
        ) : (
          <div className="px-5 py-4">{t('orders:listIsEmpty')}</div>
        )}
      </div>
    )
  },
)

export {
  getVenueStaticPaths as getStaticPaths,
  getVenueStaticProps as getStaticProps,
} from '@/services/venue'
